// Browser side of push notifications. The service worker (public/sw.js) receives and shows them; this file asks for
// permission and creates / removes the device's subscription with the push service its browser uses.
import { isStandalone } from './pwa';

/**
 * ok          — can subscribe
 * ios-install — iPhone/iPad in a browser tab: Apple only allows push for a site added to the home screen (iOS 16.4+)
 * denied      — the person blocked notifications for this site in the browser's settings
 * unsupported — this browser can't
 */
export type PushSupport = 'ok' | 'ios-install' | 'denied' | 'unsupported';

export const isIos = () => /iPhone|iPad|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

export function pushSupport(): PushSupport {
  if (typeof window === 'undefined') return 'unsupported';
  if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) return isIos() && !isStandalone() ? 'ios-install' : 'unsupported';
  if (Notification.permission === 'denied') return 'denied';
  return 'ok';
}

export const SUPPORT_HINT: Record<Exclude<PushSupport, 'ok'>, string> = {
  'ios-install': 'באייפון התראות עובדות רק מהאפליקציה שעל מסך הבית: בספארי לוחצים על "שיתוף" ← "הוספה למסך הבית", ופותחים את Piper משם.',
  denied: 'ההתראות חסומות לאתר הזה. אפשר לאשר אותן בהגדרות הדפדפן (או בהגדרות הטלפון ← התראות) ולנסות שוב.',
  unsupported: 'הדפדפן הזה לא תומך בהתראות. נסו Chrome, Edge, Firefox או Safari עדכני.',
};

function toBytes(b64url: string): Uint8Array<ArrayBuffer> {
  const s = atob(b64url.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - (b64url.length % 4)) % 4));
  const out = new Uint8Array(s.length);
  for (let i = 0; i < s.length; i++) out[i] = s.charCodeAt(i);
  return out;
}

function sameKey(a: ArrayBuffer | null, b64url: string): boolean {
  if (!a) return false;
  const x = new Uint8Array(a), y = toBytes(b64url);
  return x.length === y.length && x.every((v, i) => v === y[i]);
}

/** This device's current subscription, if any. */
export async function currentSubscription(): Promise<PushSubscription | null> {
  if (pushSupport() !== 'ok' && pushSupport() !== 'denied') return null;
  const reg = await navigator.serviceWorker.ready;
  return reg.pushManager.getSubscription();
}

/**
 * Ask for permission (must run straight from a tap — iOS refuses otherwise) and subscribe with Piper's public key.
 * A subscription made with another key (an older one) is replaced.
 */
export async function subscribe(vapidPublicKey: string): Promise<PushSubscriptionJSON> {
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') throw new Error(permission === 'denied' ? SUPPORT_HINT.denied : 'לא אושרו התראות');
  const reg = await navigator.serviceWorker.ready;
  let sub = await reg.pushManager.getSubscription();
  if (sub && !sameKey(sub.options.applicationServerKey, vapidPublicKey)) { await sub.unsubscribe(); sub = null; }
  sub ??= await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: toBytes(vapidPublicKey) });
  return sub.toJSON();
}

export async function unsubscribe(): Promise<string | null> {
  const sub = await currentSubscription();
  if (!sub) return null;
  const endpoint = sub.endpoint;
  await sub.unsubscribe().catch(() => false);
  return endpoint;
}
